import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Rating,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import { fetchJson } from '../../../utils/fetchJson';

export default function StaffRatingsPage() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [phaseFilter, setPhaseFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await fetchJson('/api/staff-management/evaluations/ratings-summary/all-phases', { method: 'GET' });
        const list = resp?.ratings || [];
        if (!cancelled) setRows(Array.isArray(list) ? list : []);
      } catch (_) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const phaseOptions = useMemo(() => {
    const set = new Set();
    (rows || []).forEach((r) => set.add(String(r.phaseId ?? 'null')));
    return Array.from(set);
  }, [rows]);

  const filtered = useMemo(() => {
    if (phaseFilter === 'all') return rows || [];
    return (rows || []).filter((r) => String(r.phaseId ?? 'null') === String(phaseFilter));
  }, [rows, phaseFilter]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress size={56} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Card>
        <CardHeader
          title="Staff Ratings"
          subheader="Average ratings per staff member (excluding School Admins)"
          action={
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Phase</InputLabel>
              <Select value={phaseFilter} label="Phase" onChange={(e) => setPhaseFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                {phaseOptions.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p === 'null' ? 'No phase' : `Phase #${p}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }
        />
        <CardContent>
          {filtered.length === 0 ? (
            <Alert severity="info">No staff ratings available for the current academic context.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Staff</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Phase</TableCell>
                    <TableCell align="center">Avg Rating</TableCell>
                    <TableCell align="right">Evaluations</TableCell>
                    <TableCell align="right">Submitted</TableCell>
                    <TableCell>Last Evaluation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={`${r.staffId}:${String(r.phaseId ?? 'null')}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{r.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={r.role || 'Staff'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{r.phaseId == null ? '—' : `#${r.phaseId}`}</TableCell>
                      <TableCell align="center">
                        {r.averageRating != null ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={700}>{Number(r.averageRating).toFixed(1)}</Typography>
                            <Rating value={Number(r.averageRating)} readOnly precision={0.1} size="small" />
                          </Box>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">{Number(r.evaluationCount || 0)}</TableCell>
                      <TableCell align="right">{Number(r.submittedCount || 0)}</TableCell>
                      <TableCell>{r.lastEvaluationDate || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
